import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { ChevronDown, ChevronRight, FileText, History } from "lucide-react";

const ProofRow = ({ p, testId }) => (
  <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-md border bg-white" data-testid={testId}>
    <div className="min-w-0">
      <p className="text-sm font-medium truncate">{p.description || "Pengeluaran"}</p>
      <p className="text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">{p.plate_number || "-"}</span>
        {` · Cabang: ${p.branch || "-"} · Sopir: ${p.driver_name || "-"} · ${formatDate(p.uploaded_at)}`}
      </p>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      {p.file_id && (
        <a href={fileUrl(p.file_id)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-brand hover:underline text-sm" data-testid={`${testId}-nota`}>
          <FileText className="h-4 w-4" /> Nota
        </a>
      )}
      <span className="font-mono text-sm font-semibold">{formatRupiah(p.amount)}</span>
    </div>
  </li>
);

const DisburseRow = ({ r, showName }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-lg border" data-testid={`vch-row-${r.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-secondary/50 transition-colors" data-testid={`vch-toggle-${r.id}`}>
        <div className="flex items-start gap-2 min-w-0">
          {open ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />}
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{r.reason || "Pencairan kas"}</p>
            <p className="text-xs text-muted-foreground">{formatDate(r.disbursed_at)}{showName ? ` · ${r.requester_name}` : ""} · {r.proofs.length} pengeluaran</p>
          </div>
        </div>
        <div className="flex gap-4 text-xs font-mono">
          <span>Cair <b data-testid={`vch-cair-${r.id}`}>{formatRupiah(r.approved_amount)}</b></span>
          <span className="text-emerald-700">Terpakai <b data-testid={`vch-used-${r.id}`}>{formatRupiah(r.used)}</b></span>
          <span className="text-rose-600">Sisa <b data-testid={`vch-remaining-${r.id}`}>{formatRupiah(r.remaining)}</b></span>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3">
          {r.proofs.length === 0
            ? <p className="text-xs text-muted-foreground py-2" data-testid={`vch-empty-${r.id}`}>Belum ada pengeluaran yang memotong kas ini.</p>
            : <ul className="space-y-1.5">{r.proofs.map((p, i) => <ProofRow key={i} p={p} testId={`vch-proof-${r.id}-${i}`} />)}</ul>}
        </div>
      )}
    </li>
  );
};

export const VehicleCashHistory = ({ showName = false, reloadKey }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/vehicle-cash-history").then((r) => setRows(r.data)).catch(() => setRows([])); }, [reloadKey]);
  return (
    <Card className="p-6" data-testid="vehicle-cash-history">
      <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><History className="h-5 w-5 text-brand" /> Riwayat Potongan Kas</h3>
      {rows === null ? <p className="text-sm text-muted-foreground">Memuat...</p>
        : rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="vch-none">Belum ada pencairan kas.</p>
        : <ul className="space-y-2">{rows.map((r) => <DisburseRow key={r.id} r={r} showName={showName} />)}</ul>}
    </Card>
  );
};
